import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["ruid"],
  _excluded2 = ["ruid", "rpid"];
import PortalIdParser from 'PortalIdParser';
import { Flow } from 'signup-constants/Flow';
import * as paramHelpers from 'hub-http/helpers/params';
import * as urlHelpers from 'hub-http/helpers/url';
import { getFullUrl } from 'hubspot-url-utils';
import { getProxyUrl } from 'academy-common-lib/utils/url';
import { TRACK_EXAM_URL_FRAGMENT, TRACK_INTRO_URL_FRAGMENT, TRACK_COMPLETE_URL_FRAGMENT, TRACKS_URL_FRAGMENT, TRACK_RECOMMENDATIONS_URL_FRAGMENT, PLAYLIST_URL_FRAGMENT } from '../constants/Navigation';
import { OVERVIEW_MODAL_PLAYLIST_PARAM } from '../features/overview/overviewConsts';
import { getOverviewModalParams } from 'academy-common-lib/utils/url';
import { getShortContentOverviewUrl, getShortContentUrl } from '../features/shortContent/shortContentRouting';
import getLang from 'I18n/utils/getLang';
export const getTrackBaseUrl = (trackId, suffix) => {
  return `/tracks/${trackId}${suffix ? `/${suffix}` : ''}`;
};
export const getLessonBaseUrl = (lessonId, suffix) => {
  return `/lessons/${lessonId}${suffix ? `/${suffix}` : ''}`;
};

/**
 * Generates a link to a track's exam, given a trackId (Be warned - this will result in sadness if you use the id of a track which doesn't have an exam)
 * @param trackId {string} - a Track's id
 * @returns {string}
 */
export function getTrackExamPageUrl(trackId) {
  return getTrackBaseUrl(trackId, TRACK_EXAM_URL_FRAGMENT);
}

/**
 * Generates a link to a track's intro page, given a trackId (Be warned - this will result in sadness if you use the id of a track which doesn't have an intro)
 * @param trackId {string} - a Track's id
 * @returns {string}
 */
export function getTrackIntroPageUrl(trackId) {
  return getTrackBaseUrl(trackId, TRACK_INTRO_URL_FRAGMENT);
}
export function getTrackWrapUpPageUrl(trackId) {
  return getTrackBaseUrl(trackId, TRACK_COMPLETE_URL_FRAGMENT);
}
export function getTrackRecommendationsUrl(trackId) {
  return getTrackBaseUrl(trackId, TRACK_RECOMMENDATIONS_URL_FRAGMENT);
}

// https://regex101.com/r/ooNw4r/3
export const replacePortalIdInPath = ({
  portalId,
  path = window.location.pathname,
  hublet
}) => {
  const isLocal = /^local/.test(document.location.hostname);
  const newPath = path.replace(/(?:academy|learning-center)(?:.*\/(results|tracks|lessons|team|mylearning|favorites|saved-items|shortvideo)|.*)/i, `academy/${portalId ? `${portalId}/` : ''}$1`);
  const url = getFullUrl(isLocal ? 'local' : 'app', {
    hubletOverride: hublet
  });
  return `${url}${newPath}${window.location.search}`;
};
export const replacePlaceholderWithPortalId = (portalId, path) => path.replace(/\/l\//g, `/${portalId}/`);

// https://regex101.com/r/iu2wTS/1
// eslint-disable-next-line no-useless-escape
const idsMatch = /\/(tracks|lessons)\/([\d\/]+)$/;
export const getIdsFromPath = (path, keys) => {
  const [__, type, idStr] = path.match(idsMatch) || [];
  const idStrs = idStr ? idStr.split('/') : [];
  const ids = {};
  let track;
  let lesson;
  let content;
  if (type === 'tracks') {
    [track, lesson, content] = idStrs;
  } else if (type === 'lessons') {
    [lesson, content] = idStrs;
  }
  if (track) {
    ids[keys[0]] = +track;
  }
  if (lesson) {
    ids[keys[1]] = +lesson;
  }
  if (content) {
    ids[keys[2]] = +content;
  }
  return ids;
};
export const parseUrlForReferral = url => {
  const descriptor = urlHelpers.parseUrl(url);
  const _paramHelpers$parse = paramHelpers.parse(descriptor.query),
    {
      ruid
    } = _paramHelpers$parse,
    query = _objectWithoutPropertiesLoose(_paramHelpers$parse, _excluded);
  const portalId = PortalIdParser.parsePortalIdFromString(descriptor.path, PortalIdParser.pathRegex);
  const ids = getIdsFromPath(descriptor.path, ['track', 'lesson', 'content']);
  const viralParams = ruid ? {
    hubs_id: 'academy-referral-link',
    viralSourceUserId: ruid
  } : {
    hubs_id: 'academy-deep-link'
  };
  descriptor.query = paramHelpers.stringify(query);
  return Object.assign({
    show_sidebar: 'true',
    intent: 'learningCenter',
    hubs_campaign: 'hubspot-academy-referral-virality',
    hubs_medium: 'virality',
    loginRedirectUrl: urlHelpers.buildUrl(descriptor),
    viralSourcePortalId: portalId
  }, ids, viralParams);
};
export const addUrlParams = (url, toAdd) => {
  const descriptor = urlHelpers.parseUrl(url);
  const query = paramHelpers.parse(descriptor.query);
  descriptor.query = paramHelpers.stringify(Object.assign({}, query, toAdd));
  return urlHelpers.buildUrl(descriptor);
};
export const buildReferralLink = (to, ruid, allowedParams = ['language']) => {
  const searchParams = new URLSearchParams(window.location.search);
  [...searchParams.keys()].forEach(param => {
    if (!allowedParams.includes(param)) {
      searchParams.delete(param);
    }
  });
  searchParams.append('ruid', ruid.toString());
  const toPath = to && to.length && to.startsWith('/') ? to.substring(1, to.length) : to;
  const urlObject = new URL(`https://${window.location.hostname}/academy/${PortalIdParser.getPortalIdFromPath()}/${toPath}`);
  const searchBuilder = new URLSearchParams(urlObject.search);
  searchParams.forEach((value, key) => searchBuilder.set(key, value));
  urlObject.search = searchBuilder.toString();
  return urlObject.toString();
};

/**
 * Get the SignupUrlGenerator arguments to send to login. This is so we can send
 * flow, intent, and tracking params to signup when someone clicks on signup
 * after being redirected to login.
 * @param {string} href - current href
 * @returns {array} array of arguments for signup-ui-url-generator/SignupUrlGenerator
 */
const getSignupParams = href => {
  const query = parseUrlForReferral(href);
  return [Flow.Academy, {
    query
  }];
};

/**
 * Return the link to direct an unauthenticated user to
 * @param {String} href - the current window.location.href
 * @param {Boolean} isQa - whether we're on a qa domain
 * @returns {String} url
 */
export const getUnauthenticatedUserRedirect = href => {
  const signupParams = JSON.stringify(getSignupParams(href));
  return `${getFullUrl('app')}/login` + `?loginRedirectUrl=${encodeURIComponent(href)}` + `&signupParams=${encodeURIComponent(signupParams)}`;
};
export const getAwardSharingPath = trackId => `/award-share/${trackId}`;
export const getAwardModalDefaultPath = trackId => `${TRACKS_URL_FRAGMENT}/award/${trackId}`;
export const getAchievementPreviewPath = trackId => `${getAwardSharingPath(trackId)}/achievement-preview`;
export const getPublicAchievementUrl = (publicId, currentLanguage, urlSlug) => `/academy/achievements/${publicId}/${currentLanguage}/1/${urlSlug}`;
export const handleInternalReferral = ({
  href,
  sendTracking,
  replacePath
}) => {
  const descriptor = urlHelpers.parseUrl(href);
  const _paramHelpers$parse2 = paramHelpers.parse(descriptor.query),
    {
      ruid,
      rpid
    } = _paramHelpers$parse2,
    query = _objectWithoutPropertiesLoose(_paramHelpers$parse2, _excluded2);

  // Only track referral if referrer portal id is set
  if (rpid) {
    const ids = getIdsFromPath(descriptor.path, ['trackId', 'lessonId', 'contentId']);
    sendTracking(Object.assign({
      viralSourcePortalId: rpid,
      viralSourceUserId: ruid
    }, ids));
  }
  // Always clear ruid and rpid from search params
  if (ruid || rpid) {
    descriptor.hostname = '';
    descriptor.query = paramHelpers.stringify(query);
    replacePath(urlHelpers.buildUrl(descriptor));
  }
};
export const getPricingPageUrl = (portalId, hub) => `/pricing/${portalId}/${hub}`;
export const getListingUrl = ({
  type,
  isStarted,
  id,
  language,
  currentPathname = '',
  currentSearch = '',
  appearsIn,
  playlistId,
  isAssignedPlaylist = false
}) => {
  let urlRoot = '';
  const searchParams = new URLSearchParams(currentSearch);
  if (type !== 'SHORT_FORM_CONTENT' && isStarted && language) {
    searchParams.set('language', language);
  }
  if (isAssignedPlaylist) searchParams.set('isAssignedPlaylist', isAssignedPlaylist.toString());
  if (type === 'SHORT_FORM_CONTENT') {
    return isStarted ? getShortContentUrl({
      shortId: id,
      playlistId,
      search: searchParams
    }) : getShortContentOverviewUrl({
      shortId: id,
      playlistId,
      overviewType: appearsIn,
      pathname: currentPathname,
      search: searchParams
    });
  }
  if (playlistId) searchParams.set(OVERVIEW_MODAL_PLAYLIST_PARAM, playlistId.toString());
  if (type === 'TRACK') {
    urlRoot = 'tracks';
  } else if (type === 'LESSON') {
    urlRoot = 'lessons';
  }
  const pathname = isStarted ? `/${urlRoot}/${id}` : currentPathname;
  if (!isStarted) {
    const modalParams = getOverviewModalParams({
      id,
      type: type,
      language: language !== null && language !== void 0 ? language : getLang(),
      source: appearsIn
    });
    Object.entries(modalParams).forEach(([key, value]) => {
      searchParams.set(key, value);
    });
  }
  return {
    fullUrl: `${pathname}?${searchParams.toString()}`,
    pathname,
    search: searchParams.toString()
  };
};
export const getPlaylistUrl = id => {
  return PLAYLIST_URL_FRAGMENT.replace(':playlistId', id.toString());
};
export const removeTrailingSlash = pathname => {
  return pathname.length > 1 && pathname.endsWith('/') ? pathname.substring(0, pathname.length - 1) : pathname;
};
export const getTrackShareUrl = (track, userId, trackId, language) => {
  if (track && track.visibleOnLoggedOutPage && track.pagePath) {
    return getProxyUrl(`courses/${track.pagePath}`);
  }
  return buildReferralLink(`/?${new URLSearchParams(getOverviewModalParams({
    id: trackId,
    type: 'TRACK',
    language
  }))}`, userId);
};
export const getLessonShareUrl = (lesson, userId, lessonId, language) => {
  if (lesson && lesson.visibleOnLoggedOutPage && lesson.pagePath) {
    return getProxyUrl(`lessons/${lesson.pagePath}`);
  }
  return buildReferralLink(`/?${new URLSearchParams(getOverviewModalParams({
    id: lessonId,
    type: 'LESSON',
    language
  }))}`, userId);
};
export const getRelativeOverviewUrl = (currentLocation, id, type, language) => {
  const searchParams = new URLSearchParams(currentLocation.search);
  const modalParams = getOverviewModalParams({
    id,
    type,
    language
  });
  Object.entries(modalParams).forEach(([key, value]) => {
    searchParams.set(key, value);
  });
  return `${currentLocation.pathname}?${searchParams}`;
};